/**
 * Route Guard Hook
 * Centralized route protection with role-based access control
 * Last Updated: March 8, 2026
 */

import { useEffect } from 'react';
import { authService, UserRole } from '@/lib/auth';
import { logger } from '@/services/logger.service';
import { toast } from 'sonner';

export type Page =
  | 'home'
  | 'catalog'
  | 'detail'
  | 'about'
  | 'services'
  | 'contact'
  | 'calculator'
  | 'calculators'
  | 'area-alerts'
  | 'pre-approval'
  | 'agent-hub'
  | 'seller-hub'
  | 'admin-hub'
  | 'marketing-hub'
  | 'external-agent-portal'
  | 'agent-directory'
  | 'agent-profile'
  | 'agent-report'
  | 'hub'
  | 'seller'
  | 'admin'
  | 'marketing'
  | 'external-agent'
  | 'aws-diagnostic'
  | 'blog'
  | 'confirm-email'
  | 'forgot-password'
  | 'saved'
  | 'agent-login-page'
  | 'seller-login-page'
  | 'otp-form'
  | 'document-upload'
  | 'referral-landing'
  | 'competition-register'
  | 'dealdesk';

export interface RouteConfig {
  requiresAuth: boolean;
  allowedRoles?: UserRole[];
  redirectTo?: Page;
  redirectOnUnauthorized?: Page;
}

/**
 * Route configuration map
 * Defines which routes require authentication and which roles can access them
 */
export const ROUTE_CONFIG: Record<Page, RouteConfig> = {
  // Public routes - no authentication required
  home: { requiresAuth: false },
  catalog: { requiresAuth: false },
  detail: { requiresAuth: false },
  about: { requiresAuth: false },
  services: { requiresAuth: false },
  contact: { requiresAuth: false },
  calculator: { requiresAuth: false },
  calculators: { requiresAuth: false },
  'area-alerts': { requiresAuth: false },
  'pre-approval': { requiresAuth: false },
  'agent-directory': { requiresAuth: false },
  'agent-profile': { requiresAuth: false },

  // Protected routes - authentication required
  'agent-hub': {
    requiresAuth: true,
    allowedRoles: ['agent', 'admin'],
    redirectOnUnauthorized: 'home'
  },
  hub: {
    requiresAuth: true,
    allowedRoles: ['agent', 'admin'],
    redirectOnUnauthorized: 'home'
  },

  'seller-hub': {
    requiresAuth: true,
    allowedRoles: ['seller', 'admin'],
    redirectOnUnauthorized: 'home'
  },
  seller: {
    requiresAuth: true,
    allowedRoles: ['seller', 'admin'],
    redirectOnUnauthorized: 'home'
  },

  'admin-hub': {
    requiresAuth: true,
    allowedRoles: ['admin'],
    redirectOnUnauthorized: 'home'
  },
  admin: {
    requiresAuth: true,
    allowedRoles: ['admin'],
    redirectOnUnauthorized: 'home'
  },

  'marketing-hub': {
    requiresAuth: true,
    allowedRoles: ['marketing', 'admin'],
    redirectOnUnauthorized: 'home'
  },
  marketing: {
    requiresAuth: true,
    allowedRoles: ['marketing', 'admin'],
    redirectOnUnauthorized: 'home'
  },

  'external-agent-portal': {
    requiresAuth: true,
    allowedRoles: ['external-agent', 'admin'],
    redirectOnUnauthorized: 'home'
  },
  'external-agent': {
    requiresAuth: true,
    allowedRoles: ['external-agent', 'admin'],
    redirectOnUnauthorized: 'home'
  },

  'agent-report': {
    requiresAuth: true,
    allowedRoles: ['agent', 'admin'],
    redirectOnUnauthorized: 'home'
  },

  'aws-diagnostic': {
    requiresAuth: true,
    allowedRoles: ['admin'],
    redirectOnUnauthorized: 'home'
  },

  // Blog
  blog: { requiresAuth: false },

  // Public auth-related pages
  'confirm-email': { requiresAuth: false },
  'forgot-password': { requiresAuth: false },
  'saved': { requiresAuth: false },

  // F-011/F-012: Direct URL login pages — public, no auth required
  'agent-login-page': { requiresAuth: false },
  'seller-login-page': { requiresAuth: false },

  // F-133/F-132/F-138: Public token pages — no auth required
  'otp-form': { requiresAuth: false },
  'document-upload': { requiresAuth: false },
  'referral-landing': { requiresAuth: false },
  'competition-register': { requiresAuth: false },

  // DealDesk Hub — deal-coordinator role only
  'dealdesk': {
    requiresAuth: true,
    allowedRoles: ['deal-coordinator', 'admin'],
    redirectOnUnauthorized: 'home'
  },
};

/**
 * Check if user can access a route
 */
export function canAccessRoute(page: Page, userRole?: UserRole): boolean {
  const config = ROUTE_CONFIG[page];

  // Public route - always accessible
  if (!config.requiresAuth) {
    return true;
  }

  // Protected route - requires authentication
  if (!userRole) {
    return false;
  }

  // Check role-based access
  if (config.allowedRoles && !config.allowedRoles.includes(userRole)) {
    return false;
  }

  return true;
}

/**
 * Get redirect page for unauthorized access
 */
export function getUnauthorizedRedirect(page: Page): Page {
  const config = ROUTE_CONFIG[page];
  return config.redirectOnUnauthorized || 'home';
}

/**
 * Route guard hook
 * Returns whether navigation should be allowed and handles redirects
 */
export function useRouteGuard(
  currentPage: Page,
  onNavigate: (page: Page) => void,
  showAgentLogin?: () => void,
  showLogin?: () => void
) {
  useEffect(() => {
    const user = authService.getCurrentUser();
    const config = ROUTE_CONFIG[currentPage];

    // Unknown route or public route — allow access
    if (!config || !config.requiresAuth) {
      return;
    }

    // Protected route - check authentication
    if (!user) {
      logger.warn('Unauthorized access attempt', {
        page: currentPage,
        reason: 'not_authenticated'
      });

      // Show appropriate login modal
      if (currentPage === 'agent-hub' || currentPage === 'hub' || currentPage === 'agent-report') {
        toast.error('Please login as an agent to access the Agent Hub');
        showAgentLogin?.();
      } else {
        toast.error('Please login to access this page');
        showLogin?.();
      }

      // Redirect to home and update URL
      onNavigate('home');
      window.history.replaceState(null, '', '/');
      return;
    }

    // Check role-based access
    if (config.allowedRoles && !config.allowedRoles.includes(user.role)) {
      logger.warn('Unauthorized access attempt', {
        page: currentPage,
        userRole: user.role,
        allowedRoles: config.allowedRoles,
        reason: 'insufficient_permissions'
      });

      toast.error(`You don't have permission to access this page`);

      // Redirect to appropriate page based on role
      const redirectPage = getUnauthorizedRedirect(currentPage);
      onNavigate(redirectPage);

      // Update browser URL so it reflects the actual page (not the unauthorized route)
      window.history.replaceState(null, '', '/');
      return;
    }

    // Access granted
    logger.debug('Route access granted', {
      page: currentPage,
      userRole: user.role
    });
  }, [currentPage, onNavigate, showAgentLogin, showLogin]);
}

/**
 * Check if navigation should be allowed
 * Use before calling onNavigate
 */
export function checkRouteAccess(
  targetPage: Page,
  showAgentLogin?: () => void,
  showLogin?: () => void
): boolean {
  const user = authService.getCurrentUser();
  const config = ROUTE_CONFIG[targetPage];

  // Public route - allow
  if (!config.requiresAuth) {
    return true;
  }

  // Protected route - check auth
  if (!user) {
    logger.warn('Navigation blocked - not authenticated', {
      targetPage
    });

    // Show appropriate login modal
    if (targetPage === 'agent-hub' || targetPage === 'hub' || targetPage === 'agent-report') {
      toast.error('Please login as an agent to access the Agent Hub');
      showAgentLogin?.();
    } else {
      toast.error('Please login to access this page');
      showLogin?.();
    }

    return false;
  }

  // Check role-based access
  if (config.allowedRoles && !config.allowedRoles.includes(user.role)) {
    logger.warn('Navigation blocked - insufficient permissions', {
      targetPage,
      userRole: user.role,
      allowedRoles: config.allowedRoles
    });

    toast.error(`You don't have permission to access this page`);
    return false;
  }

  return true;
}
